import { Component, Input, Output, EventEmitter, OnDestroy, OnInit, ViewChild, ElementRef, OnChanges, SimpleChanges, AfterViewInit } from '@angular/core';
import { FrontendResult } from '../../../_interfaces/frontend-result';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { RecordingState } from '../../../_enums/recording-state.enum';
import { RecorderService } from '../../../_services/recorder.service';
import { AuthService } from '../../../_services/auth.service';
import { Subject, takeUntil } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { TooltipAlign, TooltipDirective } from '../tooltip/tooltip.directive';

export type RecordControlAction = 'start' | 'end';
export type RecordButtonMode = 'dictate' | 'smartDictation';

@Component({
  selector: 'app-record-button',
  standalone: true,
  imports: [CommonModule, MatIconModule, TranslateModule, TooltipDirective],
  templateUrl: './record-button.component.html',
  styleUrls: ['./record-button.component.scss']
})
export class RecordButtonComponent implements OnInit, OnDestroy, OnChanges, AfterViewInit {
  @Input() dictationId: string | null = null;
  @Input() audioPayload: any = null;
  @Input() chunkDurationSeconds: number = 0.2;
  @Input() deviceId: string = '';
  @Input() disabled: boolean = false;
  @Input() disabledTooltip: string | null = null;
  @Input() saveUtterances: boolean = false;
  @Input() mode: RecordButtonMode = 'dictate';

  @Output() transcript = new EventEmitter<FrontendResult>();
  /** Fired when this click starts/resumes recording, or ends the current take. */
  @Output() recordControlClicked = new EventEmitter<RecordControlAction>();

  @ViewChild('miniCanvas') miniCanvas?: ElementRef<HTMLCanvasElement>;

  RecordingState = RecordingState;
  state: RecordingState = RecordingState.Disconnected;
  amplitude = 0;
  ready = false;
  isProcessing = false;

  private destroy$ = new Subject<void>();
  private rafId: number | null = null;

  constructor(private recorder: RecorderService, private auth: AuthService) { }

  ngOnInit(): void {
    // Attempt to connect so we can show Initiated blue state when ready
    this.connectIfNeeded();
    this.recorder.recordingState$.pipe(takeUntil(this.destroy$)).subscribe(s => {
      console.log('recordingState', s);
      this.state = s;
      if (s === RecordingState.Recording) {
        this.startMiniViz();
      } else {
        this.stopMiniViz();
      }
    });
    this.recorder.amplitude$.pipe(takeUntil(this.destroy$)).subscribe(a => this.amplitude = a || 0);
    this.recorder.recorderReady$.pipe(takeUntil(this.destroy$)).subscribe(r => this.ready = !!r);
    this.recorder.frontendResult$.pipe(takeUntil(this.destroy$)).subscribe((result) => {
      if (result) {
        this.transcript.emit(result);
      }
    });
  }

  ngAfterViewInit(): void {
    if (this.state === RecordingState.Recording) {
      this.startMiniViz();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['audioPayload'] && !changes['audioPayload'].firstChange) ||
      (changes['dictationId'] && !changes['dictationId'].firstChange) ||
      (changes['saveUtterances'] && !changes['saveUtterances'].firstChange)) {
      // Try connect again when inputs become available
      void this.connectIfNeeded();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.stopMiniViz();
  }

  private async connectIfNeeded(): Promise<void> {
    if (this.ready) return;
    // Require inputs so we don't init with null
    if (!this.audioPayload && !this.dictationId) return;
    const user = this.auth.getUser();
    const key = user?.settings?.nlpAccessKey;
    if (!key) return;
    try {
      await this.recorder.init(key, {
        audio: this.audioPayload,
        isDictation: true,
        saveUtterances: this.saveUtterances,
      });
    } catch { }
  }

  async ensureConnected(): Promise<void> {
    if (!this.ready) {
      await this.connectIfNeeded();
    }
  }

  async onClick(): Promise<void> {
    if (this.disabled || this.isProcessing) {
      return;
    }
    
    this.isProcessing = true;
    try {
      switch (this.state) {
        case RecordingState.Disconnected:
          await this.ensureConnected();
          break;
        case RecordingState.Initiated:
        case RecordingState.Paused:
          this.recordControlClicked.emit('start');
          await this.ensureConnected();
          try {
            await this.recorder.start(this.deviceId);
          } catch { }
          break;
        case RecordingState.Loading:
          break;
        case RecordingState.Recording:
          this.recordControlClicked.emit('end');
          try {
            await this.recorder.pause();
          } catch { }
          break;
        default:
          break;
      }
    } finally {
      this.isProcessing = false;
    }
  }

  get bgClass(): string {
    if (this.disabled) {
      return 'rec-bg-disabled';
    }
    switch (this.state) {
      case RecordingState.Recording:
        return 'rec-bg-recording';
      case RecordingState.Paused:
        return 'rec-bg-paused';
      case RecordingState.Loading:
        return 'rec-bg-loading';
      case RecordingState.Initiated:
        return 'rec-bg-initiated';
      case RecordingState.Disconnected:
        return 'rec-bg-disconnected';
      default:
        return 'rec-bg-ready';
    }
  }

  private startMiniViz(): void {
    if (!this.miniCanvas?.nativeElement) return;
    if (this.rafId) return;
    const canvas = this.miniCanvas.nativeElement;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const barCount = 4;
    const draw = () => {
      const width = canvas.width;
      const height = canvas.height;
      ctx.clearRect(0, 0, width, height);
      const barWidth = 3;
      const spacing = 2;
      const totalWidth = (barWidth + spacing) * barCount - spacing;
      const startX = (width - totalWidth) / 2;
      ctx.fillStyle = 'rgba(255,255,255,0.85)';
      const amp = this.state === RecordingState.Recording ? this.amplitude : 0;
      for (let i = 0; i < barCount; i++) {
        const x = startX + i * (barWidth + spacing);
        const peak = (i === 1 || i === 2) ? 1.0 : 0.6; // taller inner bars
        const minHeight = 2;
        const maxHeight = height * 0.95 * peak;
        const h = Math.max(minHeight, amp * maxHeight * (0.7 + Math.random() * 0.3));
        const y = (height - h) / 2;
        ctx.fillRect(x, y, barWidth, h);
      }
      this.rafId = requestAnimationFrame(draw);
    };
    draw();
  }

  private stopMiniViz(): void {
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    const canvas = this.miniCanvas?.nativeElement;
    const ctx = canvas?.getContext('2d');
    if (canvas && ctx) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
  }

  get tooltipText(): string {
    if (this.disabled) {
      return this.disabledTooltip || 'Dictation disabled';
    }
    if (this.state === RecordingState.Recording) {
      return 'Stop';
    }
    if (this.mode === 'smartDictation') {
      return 'Dictate & AI will fill the document';
    }
    return 'Dictate';
  }

  get tooltipAlignment(): TooltipAlign {
    switch (this.tooltipText) {
      case 'Stop':
      case 'Dictate':
        return 'center'
      default:
        return 'end'
    }
  }
}


